import liveswitch from "fm.liveswitch";
import ChannelConnectionEvents from "./ChannelConnectionEvents";
import ConnectionData from "./ConnectionData";
import CanvasDrawing from "../drawing/CanvasDrawing";
import { generateRandomColor } from "../utils/ColorUtils";
import { v4 } from "uuid";

export default class SFUConnection {
    private channel?: liveswitch.Channel;
    private localMedia?: liveswitch.LocalMedia;
    private videoDom!: HTMLElement | null;
    private layoutManager!: liveswitch.DomLayoutManager;

    private focusedView: string | null = null;
    private unfocusedOverlays: ConnectionData[] = [];
    private screenshareOverlay?: ConnectionData;
    public readonly canvasDrawing: CanvasDrawing;

    private upstreamConnection?: liveswitch.SfuUpstreamConnection;

    public connections: {
        [key: string]: ConnectionData;
    } = {};

    constructor(private events: ChannelConnectionEvents) {
        this.canvasDrawing = new CanvasDrawing();
    }

    public openConnection(
        channel: liveswitch.Channel,
        localMedia: liveswitch.LocalMedia
    ): void {
        this.channel = channel;
        this.localMedia = localMedia;

        if (!this.videoDom) {
            this.videoDom = document.getElementById("video") as HTMLElement;
            if (!this.videoDom) {
                throw new Error("Couldn't find Video element in page");
            }
            this.layoutManager = new liveswitch.DomLayoutManager(this.videoDom);
            this.layoutManager.setMode(liveswitch.LayoutMode.Inline);
            this.layoutManager.addOnLayout((_) => {
                console.log("Layout updated, connections:", Object.values(this.connections));
                this.events.notify("onvideolayout", Object.values(this.connections));
            });
        }

        // Handle both SFU and MCU connections
        this.channel.addOnRemoteUpstreamConnectionOpen((connectionInfo) => {
            console.log("Remote connection opened:", connectionInfo.getType(), connectionInfo.getUserAlias());
            if (connectionInfo.getType() === "sfu") {
                this.openDownstreamConnection(connectionInfo);
            } else if (connectionInfo.getType() === "mcu") {
                this.openMcuDownstreamConnection(connectionInfo);
            }
        });

        this.openUpstreamConnection();

        // Handle existing connections
        for (let remoteConnectionInfo of this.channel.getRemoteUpstreamConnectionInfos()) {
            console.log("Existing remote connection:", remoteConnectionInfo.getType(), remoteConnectionInfo.getUserAlias());
            if (remoteConnectionInfo.getType() === "sfu") {
                this.openDownstreamConnection(remoteConnectionInfo);
            } else if (remoteConnectionInfo.getType() === "mcu") {
                this.openMcuDownstreamConnection(remoteConnectionInfo);
            }
        }

        this.events.addListener("onscreenshareupdate", (overlay: ConnectionData) => {
            if (this.focusedView) {
                this.focusView(null);
            }
            if (this.screenshareOverlay && !overlay) {
                this.removeConnection(this.screenshareOverlay.id);
                this.screenshareOverlay = undefined;
            } else if (overlay && overlay.roles && overlay.roles.includes("screenshare")) {
                this.screenshareOverlay = overlay;
                this.addConnection(overlay);
            }
        });
    }

    public closeConnection(): void {
        this.upstreamConnection?.close();
    }

    public toggleLocalVideo(): boolean {
        if (!this.upstreamConnection) {
            console.error(
                "Upstream connection must be set before toggling local video."
            );
            return false;
        }

        const config: liveswitch.ConnectionConfig =
            this.upstreamConnection.getConfig();
        const enabled = !config.getLocalVideoMuted();

        config.setLocalVideoMuted(enabled);
        this.upstreamConnection.update(config);

        return enabled;
    }

    public toggleLocalAudio(): boolean {
        if (!this.upstreamConnection) {
            console.error(
                "Upstream connection must be set before toggling local audio."
            );
            return false;
        }

        const config: liveswitch.ConnectionConfig =
            this.upstreamConnection.getConfig();
        const enabled = !config.getLocalAudioMuted();

        config.setLocalAudioMuted(enabled);
        this.upstreamConnection.update(config);

        return enabled;
    }

    public toggleFocusView(viewId: string) {
        if (!this.focusedView) {
            this.unfocusedOverlays = Object.values(this.connections).filter(x => x.id !== viewId);

            // Disable all other views
            for (const overlay of this.unfocusedOverlays) {
                this.removeConnection(overlay.id);
            }
        } else {
            // Restore all previously removed views
            for (const overlay of this.unfocusedOverlays) {
                this.addConnection(overlay);
            }

            this.unfocusedOverlays = [];
        }

        this.focusedView = this.focusedView !== null ? null : viewId;
    }

    public focusView(viewId: string | null) {
        if (viewId) {
            this.unfocusedOverlays = Object.values(this.connections).filter(
                (x) => x.id !== viewId
            );

            // Disable all other views
            for (const overlay of this.unfocusedOverlays) {
                this.removeConnection(overlay.id);
            }

            this.events.notify("onviewfocus", this.connections[viewId]);
        } else {
            // Restore all previously removed views
            for (const overlay of this.unfocusedOverlays) {
                this.addConnection(overlay);
            }

            this.unfocusedOverlays = [];

            this.events.notify("onviewfocus", null);
        }

        this.focusedView = viewId;
    }

    private addConnection(connectionData: ConnectionData) {
        console.log("Adding connection:", connectionData.userAlias);
        this.connections[connectionData.id] = connectionData;

        const video = connectionData.view.querySelector('video');
        if (video) {
            video.style.transform = 'scaleX(-1)';
        }
        
        if (connectionData.isLocal && !connectionData.isScreen) {
            this.layoutManager.setLocalView(connectionData.view);
        } else {
            this.layoutManager.addRemoteView(
                connectionData.id,
                connectionData.view
            );
        }
    }

    private removeConnection(id: string) {
        const connection = this.connections[id];

        if (connection) {
            if (connection.roles.includes("local")) {
                this.layoutManager.unsetLocalView();
            } else {
                this.layoutManager.removeRemoteView(connection.id);
            }
            delete this.connections[id];
        }
    }

    private openUpstreamConnection() {
        const audioStream = new liveswitch.AudioStream(this.localMedia!);
        const videoStream = new liveswitch.VideoStream(this.localMedia!);
        const dataStream = new liveswitch.DataStream([
            this.canvasDrawing.sendDataChannel
        ]);

        this.upstreamConnection = this.channel!.createSfuUpstreamConnection(
            audioStream,
            videoStream,
            dataStream
        );

        this.upstreamConnection.addOnStateChange((conn) => {
            const state = conn.getState();

            console.log(
                `Upstream connection is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            const size = this.localMedia!.getVideoSize();
            const messageId = v4();
            const connectionData: ConnectionData = {
                id: conn.getId(),
                userId: conn.getUserId(),
                deviceId: this.channel!.getDeviceId().replace(/-/g, ''),
                userAlias: this.channel!.getUserAlias(),
                roles: [...this.channel!.getClientRoles(), "local", `m_${messageId}`],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: true,
                isScreen: false,
                isXR: false,
                view: this.localMedia!.getView(),
                videoWidth: size.getWidth(),
                videoHeight: size.getHeight(),
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                this.addConnection(connectionData);
                this.events.notify("onconnectionopen", connectionData);

                this.canvasDrawing.myConnectionData = connectionData;
            } else if (state === liveswitch.ConnectionState.Closing || state === liveswitch.ConnectionState.Failing) {
                this.events.notify("onconnectionclose", connectionData);
            } else if (state === liveswitch.ConnectionState.Failed) {
                this.openUpstreamConnection();
            }
        });

        this.upstreamConnection.open();
    }

    private openDownstreamConnection(
        remoteConnectionInfo: liveswitch.ConnectionInfo
    ) {
        console.log("Opening downstream connection for:", remoteConnectionInfo.getUserAlias());
        
        const remoteMedia = new liveswitch.RemoteMedia(true, true);
        const audioStream = new liveswitch.AudioStream(remoteMedia);
        const videoStream = new liveswitch.VideoStream(remoteMedia);
        const dataStream = new liveswitch.DataStream([
            this.canvasDrawing.createReceiveDataChannel()
        ]);

        const downstreamConnection =
            this.channel!.createSfuDownstreamConnection(
                remoteConnectionInfo,
                audioStream,
                videoStream,
                dataStream
            );

        downstreamConnection.addOnStateChange((conn) => {
            const state = conn.getState();

            console.log(
                `Downstream connection ${conn.getId()} is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            const sendEncoding = remoteConnectionInfo.getVideoStream().getSendEncodings()[0];
            const width = sendEncoding.getWidth();
            const height = sendEncoding.getHeight();
            
            const isScreen =
                remoteConnectionInfo.getType() === "sfu" &&
                remoteConnectionInfo.getVideoStream().getSendContent() === "screen";
            const isXR = false;//remoteConnectionInfo.getClientRoles().includes("hololens");

            const connectionData: ConnectionData = {
                id: remoteConnectionInfo.getId(),
                userId: remoteConnectionInfo.getUserId(),
                deviceId: remoteConnectionInfo.getDeviceId(),
                userAlias: remoteConnectionInfo.getUserAlias(),
                roles: ["remote"],//[...remoteConnectionInfo.getClientRoles(), "remote"],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: false,
                isScreen,
                isXR,
                view: remoteMedia.getView(),
                videoWidth: width,
                videoHeight: height
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                if (this.focusedView) {
                    this.unfocusedOverlays.push(connectionData);
                } else {
                    this.addConnection(connectionData);
                }

                this.events.notify("onconnectionopen", connectionData);
                if (isScreen) {
                    this.events.notify("onscreenshareupdate", connectionData);
                }
            } else if (
                state === liveswitch.ConnectionState.Closing ||
                state === liveswitch.ConnectionState.Failing
            ) {
                const id = remoteConnectionInfo.getId();
                const shouldUnfocus = this.focusedView === id;
                const shouldRemoveConnection =
                    shouldUnfocus || !this.focusedView;

                if (shouldUnfocus) {
                    this.focusView(null);
                } else if (this.focusedView) {
                    this.unfocusedOverlays = this.unfocusedOverlays.filter(
                        (x) => x.id !== id
                    );
                    delete this.connections[id];
                }

                if (shouldRemoveConnection) {
                    this.removeConnection(id);
                }

                this.events.notify("onconnectionclose", connectionData);

                if (isScreen) {
                    this.events.notify("onscreenshareupdate", null);
                }

                remoteMedia.destroy();
            }
        });

        downstreamConnection.open();
    }

    private openMcuDownstreamConnection(remoteConnectionInfo: liveswitch.ConnectionInfo) {
        console.log("Opening MCU downstream connection for:", remoteConnectionInfo.getUserAlias());
        
        const remoteMedia = new liveswitch.RemoteMedia(true, true);
        const audioStream = new liveswitch.AudioStream(remoteMedia);
        const videoStream = new liveswitch.VideoStream(remoteMedia);

        const downstreamConnection = this.channel!.createSfuDownstreamConnection(
            remoteConnectionInfo,
            audioStream,
            videoStream
        );

        downstreamConnection.addOnStateChange((conn: liveswitch.ManagedConnection) => {
            const state = conn.getState();

            console.log(
                `MCU Downstream connection ${conn.getId()} is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            const sendEncoding = remoteConnectionInfo.getVideoStream().getSendEncodings()[0];
            const width = sendEncoding.getWidth();
            const height = sendEncoding.getHeight();
            
            const isScreen =
                remoteConnectionInfo.getType() === "sfu" &&
                remoteConnectionInfo.getVideoStream().getSendContent() === "screen";
            const isXR = false;//remoteConnectionInfo.getClientRoles().includes("hololens");

            const connectionData: ConnectionData = {
                id: remoteConnectionInfo.getId(),
                userId: remoteConnectionInfo.getUserId(),
                deviceId: remoteConnectionInfo.getDeviceId(),
                userAlias: remoteConnectionInfo.getUserAlias(),
                roles: ["mcu"],//[...remoteConnectionIfocusViewnfo.getClientRoles(), "remote", "mcu"],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: false,
                isScreen,
                isXR,
                view: remoteMedia.getView(),
                videoWidth: width,
                videoHeight: height
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                if (this.focusedView) {
                    this.unfocusedOverlays.push(connectionData);
                } else {
                    this.addConnection(connectionData);
                }

                this.events.notify("onconnectionopen", connectionData);
                if (isScreen) {
                    this.events.notify("onscreenshareupdate", connectionData);
                }
            } else if (
                state === liveswitch.ConnectionState.Closing ||
                state === liveswitch.ConnectionState.Failing
            ) {
                const id = remoteConnectionInfo.getId();
                const shouldUnfocus = this.focusedView === id;
                const shouldRemoveConnection =
                    shouldUnfocus || !this.focusedView;

                if (shouldUnfocus) {
                    this.focusView(null);
                } else if (this.focusedView) {
                    this.unfocusedOverlays = this.unfocusedOverlays.filter(
                        (x) => x.id !== id
                    );
                    delete this.connections[id];
                }

                if (shouldRemoveConnection) {
                    this.removeConnection(id);
                }

                this.events.notify("onconnectionclose", connectionData);

                if (isScreen) {
                    this.events.notify("onscreenshareupdate", null);
                }

                remoteMedia.destroy();
            }
        });

        downstreamConnection.open();
    }
}
